/**
 * Waveforms for the editor's timeline: the sound of a clip as one peak per hundredth of
 * a second, so the timeline can draw it at any zoom and show where to cut.
 *
 * The audio is decoded from the bytes the editor already holds (never fetched again),
 * at a low sample rate so a long video does not fill memory, and the peaks are kept in
 * this browser so a clip is decoded once.
 */
import { getEditorMedia, putEditorMedia } from './editorMediaStore';

export const PEAKS_PER_SEC = 100;
/** Enough for peaks every 10 ms, and light enough for long videos. */
const DECODE_RATE = 8000;
/** Loudness shown, in decibels below full scale; quieter is drawn flat. */
const RANGE_DB = 48;

/**
 * The loudest sample in each slice of `perSec` per second, across channels, as a
 * fraction of full scale (not normalised, so quiet clips look quiet). Pure, for tests.
 */
export const peaksPerSecond = (channels: Float32Array[], sampleRate: number, perSec = PEAKS_PER_SEC): Float32Array => {
    const length = channels[0]?.length ?? 0;
    if (!length || sampleRate <= 0) return new Float32Array(0);
    const count = Math.max(1, Math.ceil((length / sampleRate) * perSec));
    const out = new Float32Array(count);
    const perPeak = sampleRate / perSec;
    for (let p = 0; p < count; p++) {
        const start = Math.floor(p * perPeak);
        const end = Math.min(length, Math.max(start + 1, Math.floor((p + 1) * perPeak)));
        let peak = 0;
        for (const channel of channels) {
            for (let i = start; i < end; i++) {
                const v = Math.abs(channel[i]);
                if (v > peak) peak = v;
            }
        }
        out[p] = Math.min(1, peak);
    }
    return out;
};

/** How tall to draw a peak, 0 to 1: on a decibel scale, so speech and quiet music still show. */
export const peakHeight = (peak: number): number => {
    if (peak <= 0) return 0;
    const db = 20 * Math.log10(peak);
    return Math.max(0, Math.min(1, 1 + db / RANGE_DB));
};

/** The loudest peak between two times, for one bar of the drawing. */
export const peakBetween = (peaks: Float32Array, fromSec: number, toSec: number): number => {
    const first = Math.max(0, Math.floor(fromSec * PEAKS_PER_SEC));
    const last = Math.min(peaks.length, Math.max(first + 1, Math.ceil(toSec * PEAKS_PER_SEC)));
    let peak = 0;
    for (let i = first; i < last; i++) if (peaks[i] > peak) peak = peaks[i];
    return peak;
};

const decode = async (blob: Blob): Promise<Float32Array> => {
    const Offline = (window as Window & { OfflineAudioContext?: typeof OfflineAudioContext; webkitOfflineAudioContext?: typeof OfflineAudioContext })
        .OfflineAudioContext
        || (window as Window & { webkitOfflineAudioContext?: typeof OfflineAudioContext }).webkitOfflineAudioContext;
    if (!Offline) throw new Error('Web Audio is not available');
    // An offline context decodes without touching audio output or autoplay rules, resampled to its rate
    const decoded = await new Offline(1, 1, DECODE_RATE).decodeAudioData(await blob.arrayBuffer());
    const channels = Array.from({ length: decoded.numberOfChannels }, (_, i) => decoded.getChannelData(i));
    return peaksPerSecond(channels, decoded.sampleRate);
};

const storeKey = (key: string) => `wave:${PEAKS_PER_SEC}:${key}`;

/**
 * The waveform of the media in `blob`, filed under `key` (the same media keeps the same
 * key across sessions). Throws when the media has no sound that can be decoded.
 */
export const loadWaveformPeaks = async (key: string, blob: Blob): Promise<Float32Array> => {
    const kept = await getEditorMedia(storeKey(key)).catch(() => null);
    if (kept) return new Float32Array(await kept.arrayBuffer());
    const peaks = await decode(blob);
    putEditorMedia(storeKey(key), new Blob([peaks.buffer as ArrayBuffer], { type: 'application/octet-stream' })).catch(() => undefined);
    return peaks;
};
